import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from './common/SafeIcon';
import RegistrationModal from './components/RegistrationModal';
import './App.css';

const { FiCheck, FiX, FiClock, FiUsers, FiTarget, FiTrendingUp, FiStar, FiGift, FiArrowRight } = FiIcons;

function App() {
  const [showModal, setShowModal] = useState(false);
  const [timeLeft, setTimeLeft] = useState({
    days: 2,
    hours: 14,
    minutes: 32,
    seconds: 45
  });

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev.seconds > 0) {
          return { ...prev, seconds: prev.seconds - 1 };
        } else if (prev.minutes > 0) {
          return { ...prev, minutes: prev.minutes - 1, seconds: 59 };
        } else if (prev.hours > 0) {
          return { ...prev, hours: prev.hours - 1, minutes: 59, seconds: 59 };
        } else if (prev.days > 0) {
          return { ...prev, days: prev.days - 1, hours: 23, minutes: 59, seconds: 59 };
        }
        return prev;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const openModal = () => setShowModal(true);
  const closeModal = () => setShowModal(false);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800">
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-900/50 to-transparent"></div>
        
        {/* Trust Strip */}
        <div className="bg-gradient-to-r from-amber-500 to-amber-600 text-black py-2">
          <div className="container mx-auto px-4">
            <div className="flex flex-wrap justify-center items-center gap-4 text-sm font-medium">
              <div className="flex items-center gap-2">
                <SafeIcon icon={FiCheck} className="text-green-700" />
                <span>100% LIVE Training</span>
              </div>
              <div className="flex items-center gap-2">
                <SafeIcon icon={FiX} className="text-red-700" />
                <span>Recording கிடையாது</span>
              </div>
              <div className="flex items-center gap-2">
                <SafeIcon icon={FiClock} className="text-blue-700" />
                <span>Limited Seats</span>
              </div>
              <div className="flex items-center gap-2">
                <SafeIcon icon={FiTarget} className="text-purple-700" />
                <span>Practical + Action-based</span>
              </div>
            </div>
          </div>
        </div>

        <div className="container mx-auto px-4 py-16 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            {/* Main Headline */}
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight"
            >
              உங்க Business-ல பணம் <span className="text-amber-400">Silent-ஆ leak</span> ஆகிட்டு இருக்கு…<br />
              <span className="text-2xl md:text-4xl text-gray-300">ஆனா எங்கன்னு உங்களுக்கு தெரியல</span>
            </motion.h1>

            {/* Sub Headline */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="bg-gradient-to-r from-blue-800/50 to-purple-800/50 p-6 rounded-2xl backdrop-blur-sm border border-blue-500/30 mb-8"
            >
              <h2 className="text-2xl md:text-3xl text-white font-semibold mb-4">
                FunnelsDone உடன் 10X Profits<br />
                4 நாட்கள் LIVE Business Workshop (Tamil)
              </h2>
              <p className="text-xl text-gray-300">
                4 நாட்களில்,<br />
                <span className="text-amber-400 font-semibold">Leads → Sales → High Profit → Recurring Income</span><br />
                இந்த 4 major business problems-யும் system-ஆ fix பண்ண கத்துக்குவீங்க
              </p>
            </motion.div>

            {/* Promise Bullets */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="grid md:grid-cols-2 gap-4 mb-8"
            >
              {[
                'Leads வந்தாலும் sale ஆகாத problem fix',
                'Sales follow-up stress இல்லாம automation',
                'Same customers-லிருந்து அதிக profit',
                'Monthly predictable income system'
              ].map((item, index) => (
                <div key={index} className="flex items-center gap-3 bg-white/10 p-4 rounded-lg backdrop-blur-sm">
                  <SafeIcon icon={FiCheck} className="text-green-400 text-xl flex-shrink-0" />
                  <span className="text-white font-medium">{item}</span>
                </div>
              ))}
            </motion.div>

            {/* CTA Button */}
            <motion.button
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              onClick={openModal}
              className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white px-8 py-4 rounded-full text-xl font-bold shadow-2xl transform hover:scale-105 transition-all duration-300 mb-8"
            >
              🔴 FREE-ஆ Register பண்ணுங்க (LIVE Workshop)
            </motion.button>

            {/* Timer */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
              className="bg-black/30 p-6 rounded-xl backdrop-blur-sm"
            >
              <p className="text-amber-400 text-lg font-semibold mb-4">Next LIVE Session ஆரம்பிக்க இன்னும்:</p>
              <div className="grid grid-cols-4 gap-4 max-w-md mx-auto">
                {[
                  { label: 'நாட்கள்', value: timeLeft.days },
                  { label: 'மணி', value: timeLeft.hours },
                  { label: 'நிமிடம்', value: timeLeft.minutes },
                  { label: 'விநாடி', value: timeLeft.seconds }
                ].map((item, index) => (
                  <div key={index} className="text-center">
                    <div className="bg-gradient-to-b from-amber-400 to-amber-600 text-black text-2xl font-bold p-3 rounded-lg">
                      {item.value.toString().padStart(2, '0')}
                    </div>
                    <p className="text-gray-300 text-sm mt-1">{item.label}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Problem Agitation Section */}
      <section className="py-16 bg-gradient-to-r from-slate-800 to-slate-900">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <motion.h2 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-3xl md:text-5xl font-bold text-white mb-12"
            >
              என்னால Business வளரல… ஆனா reason என்னன்னு தெரியல
            </motion.h2>

            <div className="grid md:grid-cols-2 gap-8 mb-12">
              {[
                'Ads podren… ROI varala',
                'Leads respond pannama miss ஆகுது',
                'Sales எல்லாம் என்னை depend பண்ணி இருக்கு',
                'One-time customers… repeat illa',
                'Income stable illa, tension அதிகம்'
              ].map((pain, index) => (
                <motion.div 
                  key={index}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-red-900/20 border border-red-500/30 p-6 rounded-lg"
                >
                  <p className="text-red-300 text-lg font-medium">{pain}</p>
                </motion.div>
              ))}
            </div>

            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-r from-amber-500/20 to-amber-600/20 border border-amber-500/50 p-8 rounded-xl"
            >
              <p className="text-2xl md:text-3xl text-amber-400 font-bold">
                Problem marketing இல்ல…<br />
                <span className="text-white">System இல்லாத business தான் real problem.</span>
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Solution Overview */}
      <section className="py-16 bg-gradient-to-r from-blue-900 to-purple-900">
        <div className="container mx-auto px-4">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-4xl mx-auto"
          >
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-8">
              இந்த 4-நாள் LIVE Workshop உங்க Business-ல இருக்குற<br />
              <span className="text-amber-400">4 Profit Leaks-யும் close பண்ணும்</span>
            </h2>
          </motion.div>
        </div>
      </section>

      {/* Day by Day Breakdown */}
      <section className="py-16 bg-gradient-to-br from-slate-900 to-slate-800">
        <div className="container mx-auto px-4">
          <div className="grid gap-12">
            {/* Day 1 */}
            <motion.div 
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-r from-blue-800/30 to-blue-900/30 p-8 rounded-2xl border border-blue-500/30"
            >
              <div className="flex flex-col md:flex-row items-start gap-8">
                <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white p-6 rounded-xl text-center min-w-[200px]">
                  <div className="text-3xl font-bold mb-2">DAY 1</div>
                  <div className="text-sm">Worth ₹9,999</div>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
                    Unlimited FREE Leads மூலம் 2X Profit
                  </h3>
                  <p className="text-red-300 text-lg mb-6 italic">
                    "Leads இல்லாததுனால business slow-ஆ இருக்கு"
                  </p>
                  <div className="grid md:grid-cols-2 gap-4">
                    {[
                      'Leads எங்க leak ஆகுது',
                      'Enquiry வந்த உடனே follow-up system',
                      'Ads, WhatsApp, Website-ல lead capture',
                      'Manual work இல்லாம automation'
                    ].map((item, index) => (
                      <div key={index} className="flex items-center gap-3">
                        <SafeIcon icon={FiCheck} className="text-green-400" />
                        <span className="text-gray-300">{item}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-6 flex flex-wrap gap-4">
                    <div className="bg-green-500/20 text-green-400 px-4 py-2 rounded-full">
                      ✔️ Lead leakage clarity
                    </div>
                    <div className="bg-green-500/20 text-green-400 px-4 py-2 rounded-full">
                      ✔️ Simple lead system blueprint
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Day 2 */}
            <motion.div 
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-r from-purple-800/30 to-purple-900/30 p-8 rounded-2xl border border-purple-500/30"
            >
              <div className="flex flex-col md:flex-row items-start gap-8">
                <div className="bg-gradient-to-br from-purple-500 to-purple-600 text-white p-6 rounded-xl text-center min-w-[200px]">
                  <div className="text-3xl font-bold mb-2">DAY 2</div>
                  <div className="text-sm">Worth ₹14,999</div>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
                    Effortless Sales Conversion மூலம் 4X Profit
                  </h3>
                  <p className="text-red-300 text-lg mb-6 italic">
                    "Leads varudhu… sale close ஆகல"
                  </p>
                  <div className="grid md:grid-cols-2 gap-4">
                    {[
                      'Offer confusion எப்படி sales-ஐ kill பண்ணுது',
                      'Buyer fear & hesitation remove பண்ணுவது',
                      'WhatsApp + Chat conversion system',
                      'Pressure இல்லாத selling'
                    ].map((item, index) => (
                      <div key={index} className="flex items-center gap-3">
                        <SafeIcon icon={FiCheck} className="text-green-400" />
                        <span className="text-gray-300">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Day 3 */}
            <motion.div 
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-r from-amber-800/30 to-amber-900/30 p-8 rounded-2xl border border-amber-500/30"
            >
              <div className="flex flex-col md:flex-row items-start gap-8">
                <div className="bg-gradient-to-br from-amber-500 to-amber-600 text-black p-6 rounded-xl text-center min-w-[200px]">
                  <div className="text-3xl font-bold mb-2">DAY 3</div>
                  <div className="text-sm">Worth ₹19,999</div>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
                    Upsells & High-Ticket Offers மூலம் 8X Profit
                  </h3>
                  <p className="text-red-300 text-lg mb-6 italic">
                    "Sales varudhu… profit குறைவு"
                  </p>
                  <div className="grid md:grid-cols-2 gap-4">
                    {[
                      'Same customer-ல இருந்து அதிக income',
                      'Upsell & premium offers structure',
                      'High-ticket confidence & clarity',
                      'Less sales → more profit model'
                    ].map((item, index) => (
                      <div key={index} className="flex items-center gap-3">
                        <SafeIcon icon={FiCheck} className="text-green-400" />
                        <span className="text-gray-300">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Day 4 */}
            <motion.div 
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-r from-green-800/30 to-green-900/30 p-8 rounded-2xl border border-green-500/30"
            >
              <div className="flex flex-col md:flex-row items-start gap-8">
                <div className="bg-gradient-to-br from-green-500 to-green-600 text-white p-6 rounded-xl text-center min-w-[200px]">
                  <div className="text-3xl font-bold mb-2">DAY 4</div>
                  <div className="text-sm">Worth ₹24,999</div>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
                    Customer Retention & Recurring Income மூலம் 16X Profit
                  </h3>
                  <p className="text-red-300 text-lg mb-6 italic">
                    "Customer once வாங்கிட்டு போயிடுறாங்க"
                  </p>
                  <div className="grid md:grid-cols-2 gap-4">
                    {[
                      'Repeat buyers system',
                      'Monthly predictable income',
                      'Membership / subscription models',
                      'Long-term business stability'
                    ].map((item, index) => (
                      <div key={index} className="flex items-center gap-3">
                        <SafeIcon icon={FiCheck} className="text-green-400" />
                        <span className="text-gray-300">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Bonus Section */}
      <section className="py-16 bg-gradient-to-r from-amber-900/20 to-amber-800/20">
        <div className="container mx-auto px-4">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-4">
              Register பண்ணினா <span className="text-amber-400">FREE-ஆ கிடைக்கற Bonus-கள்</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <motion.div 
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-blue-800/30 to-blue-900/30 p-8 rounded-2xl border border-blue-500/30"
            >
              <div className="text-center">
                <SafeIcon icon={FiGift} className="text-6xl text-amber-400 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-white mb-4">🎁 FREE AI Apps</h3>
                <ul className="text-gray-300 space-y-2 mb-6">
                  <li>• Lead follow-up automation</li>
                  <li>• WhatsApp & reminder tools</li>
                </ul>
                <div className="bg-amber-500/20 text-amber-400 px-4 py-2 rounded-full inline-block">
                  💰 Worth ₹9,999
                </div>
              </div>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="bg-gradient-to-br from-purple-800/30 to-purple-900/30 p-8 rounded-2xl border border-purple-500/30"
            >
              <div className="text-center">
                <SafeIcon icon={FiStar} className="text-6xl text-amber-400 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-white mb-4">🎁 Exponential Growth Workbook</h3>
                <ul className="text-gray-300 space-y-2 mb-6">
                  <li>• All 4 days action sheets</li>
                  <li>• AI prompts included</li>
                  <li>• Money leak calculators</li>
                </ul>
                <div className="bg-amber-500/20 text-amber-400 px-4 py-2 rounded-full inline-block">
                  💰 Worth ₹7,999
                </div>
              </div>
            </motion.div>
          </div>

          {/* Value Stack */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-12 bg-black/30 p-8 rounded-2xl max-w-md mx-auto"
          >
            <div className="text-gray-300 text-lg mb-2 line-through">Total Value: ₹86,995</div>
            <div className="text-4xl font-bold text-amber-400 mb-2">Today Access: ₹0</div>
            <div className="text-2xl text-green-400 font-bold">(FREE)</div>
          </motion.div>
        </div>
      </section>

      {/* Who Should Attend */}
      <section className="py-16 bg-gradient-to-r from-slate-800 to-slate-900">
        <div className="container mx-auto px-4">
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-3xl md:text-5xl font-bold text-white text-center mb-12"
          >
            இந்த Workshop யாருக்கு?
          </motion.h2>

          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {/* Who Should Attend */}
            <motion.div 
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-green-900/20 border border-green-500/30 p-8 rounded-2xl"
            >
              <h3 className="text-2xl font-bold text-green-400 mb-6 text-center">✔️ யாருக்கு?</h3>
              <ul className="space-y-4">
                {[
                  'Business growth serious-ஆ நினைக்குறவர்கள்',
                  'Monthly income stable ஆகணும்னு நினைக்குறவர்கள்',
                  'System build பண்ண ready-ஆ இருப்பவர்கள்'
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <SafeIcon icon={FiCheck} className="text-green-400 flex-shrink-0" />
                    <span className="text-gray-300">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Who Should Not Attend */}
            <motion.div 
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-red-900/20 border border-red-500/30 p-8 rounded-2xl"
            >
              <h3 className="text-2xl font-bold text-red-400 mb-6 text-center">❌ யாருக்கு இல்லை?</h3>
              <ul className="space-y-4">
                {[
                  'Free-aa paathutu போகிறவர்கள்',
                  'LIVE attend பண்ண முடியாதவர்கள்',
                  'Shortcut expect பண்ணுறவர்கள்'
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <SafeIcon icon={FiX} className="text-red-400 flex-shrink-0" />
                    <span className="text-gray-300">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-16 bg-gradient-to-r from-blue-900 via-purple-900 to-blue-900">
        <div className="container mx-auto px-4">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-4xl mx-auto"
          >
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-8">
              Business struggle-ஐ repeat பண்ணாதீங்க<br />
              <span className="text-amber-400">System build பண்ண ஆரம்பிங்க</span>
            </h2>

            <motion.button
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              onClick={openModal}
              className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white px-12 py-6 rounded-full text-2xl font-bold shadow-2xl transform hover:scale-105 transition-all duration-300 mb-8"
            >
              🔴 FREE-ஆ Register பண்ணுங்க (LIVE Only)
            </motion.button>

            <p className="text-amber-400 text-lg font-semibold">
              Seats close ஆனதும் registration automatically stop ஆகும்.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black py-8">
        <div className="container mx-auto px-4 text-center">
          <p className="text-gray-400">
            © Owned & Conducted by <span className="text-white font-semibold">Abdul Hakkim A</span><br />
            Founder – FunnelsDone & AI Funnels<br />
            Tamil Business Owners-க்கான Exclusive LIVE Workshop
          </p>
        </div>
      </footer>

      {/* Sticky CTA Button for Mobile */}
      <div className="fixed bottom-4 left-4 right-4 z-50 md:hidden">
        <button
          onClick={openModal}
          className="w-full bg-gradient-to-r from-red-600 to-red-700 text-white py-4 rounded-full font-bold text-lg shadow-2xl animate-pulse"
        >
          🔴 FREE Register (LIVE Only)
        </button>
      </div>

      {/* Registration Modal */}
      <RegistrationModal isOpen={showModal} onClose={closeModal} />
    </div>
  );
}

export default App;